import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api, type SessionUser } from '../api/client';
import {
  API_BASE,
  hasSessionFlag,
  refreshAccessToken,
  takeRefreshedUser,
  tokens,
} from '../api/http';
import { SessionContext, type SessionApi, type SessionState } from './session-context';

const XHR = { 'X-Requested-With': 'XMLHttpRequest' };

/** public entry pages: without the session flag there is nothing to restore there */
const PUBLIC_PATHS = ['/login', '/signup', '/lost-password', '/reset-password', '/activate'];

async function restore(): Promise<SessionState> {
  // no flag → no refresh cookie was set by this API: skip a call that can only answer 401 (it
  // would also be a console error on every anonymous visit). Protected pages still try once, so
  // sessions created before the flag existed keep working.
  if (!hasSessionFlag() && PUBLIC_PATHS.includes(window.location.pathname))
    return { status: 'anonymous' };
  const t = await refreshAccessToken();
  if (!t) return { status: 'anonymous' };
  // the refresh answer carries the user; older APIs do not, then ask /auth/me
  const user = takeRefreshedUser<SessionUser>() ?? (await api.auth.me());
  return { status: 'authenticated', user, accessToken: t };
}

/**
 * Session from the httpOnly refresh cookie: on load the SPA exchanges it for an in-memory access
 * token (never stored in localStorage). The API layer refreshes on 401 and tells us when the
 * session is gone; `initial` skips the network (tests).
 */
export function SessionProvider({
  children,
  initial,
}: {
  children: ReactNode;
  initial?: SessionState;
}) {
  const [state, setState] = useState<SessionState>(initial ?? { status: 'loading' });
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (initial?.status === 'authenticated') tokens.set(initial.accessToken);
    if (initial) return;
    let live = true;
    restore().then(
      (s) => live && setState(s),
      () => live && setState({ status: 'anonymous' }), // API unreachable → treat as logged out
    );
    return () => {
      live = false;
    };
  }, [initial]);

  useEffect(() => {
    const offExpired = tokens.onExpired(() => {
      queryClient.clear();
      setState({ status: 'anonymous' });
    });
    const offMaint = tokens.onMaintenance(() => void navigate('/maintenance'));
    return () => {
      offExpired();
      offMaint();
    };
  }, [queryClient, navigate]);

  const refresh = useCallback(async () => {
    try {
      setState(await restore());
    } catch {
      setState({ status: 'anonymous' });
    }
  }, []);

  const login = useCallback(
    async (username: string, password: string, remember: boolean) => {
      const r = await api.auth.login({ username, password, remember });
      tokens.set(r.accessToken);
      const user = await api.auth.me();
      queryClient.clear();
      setState({ status: 'authenticated', user, accessToken: r.accessToken });
      return user;
    },
    [queryClient],
  );

  const logout = useCallback(async () => {
    try {
      await fetch(`${API_BASE}/api/v2/auth/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { ...XHR, ...(tokens.get() ? { Authorization: `Bearer ${tokens.get()}` } : {}) },
      });
    } finally {
      tokens.set(null);
      queryClient.clear();
      setState({ status: 'anonymous' });
    }
  }, [queryClient]);

  const value = useMemo<SessionApi>(
    () => ({ state, login, refresh, logout }),
    [state, login, refresh, logout],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
